import Loading from 'Shared/components/Loading'
import ServerWaitButton from 'Shared/components/ServerWaitButton'
import restClient from 'Shared/hooks/restClient'
import FieldEdit from '../FieldEdit'

const ERRORS = {
  'invalid_lookup': "Please check the postcode you've entered is correct.",
  'no_decision_makers': "Sorry, we've not got anyone for you to email.",
  'other': "An error occurred. Please try again."
}

const LookupDecisionMakers = ({ decisionMakerSet, setDecisionMakers, action, onSkip }) => {
  const flowId = useSel(s => s.taker?.flow?.id || s.builder.flow.id)
  const [postcode, setPostcode] = useState('')
  const [error, setError] = useState(null)

  function lookup() {
    return restClient.get(`/api/target/flows/${flowId}/lookup_decision_makers?action_id=${action.id}&lookup=${postcode}`)
      .then(({ data }) => {
        setDecisionMakers(data.decisionMakers || [])
        setError(data.error || null)
      })
  }

  useEffect(() => {
    if (decisionMakerSet.lookupKind == 'manual') {
      lookup()
    }
  }, [decisionMakerSet])

  const getError = (slug) => {
    if (slug == 'no_decision_makers' && action.config?.noDecisionMakersFoundMessage) {
      return action.config.noDecisionMakersFoundMessage
    } 
    if (ERRORS[slug]) {
      return ERRORS[slug]
    }
    return ERRORS['other']
  }

  return <div className="lookup-decision-makers">
    <h3>Enter your postcode to email your MP</h3>
    <input
      type='text'
      className='postcode'
      placeholder='Enter your postcode'
      onChange={(e) => setPostcode(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          lookup()
        }
      }}
    />
    {error && <p className="error">{getError(error)}</p>}
    <ServerWaitButton primary onClick={lookup} disabled={postcode.length === 0}>Find my MP</ServerWaitButton>
    {error && error == 'no_decision_makers' && <ServerWaitButton primary onClick={onSkip}>Skip this action</ServerWaitButton>}
  </div >
}

const EmailDecisionMakers = ({ decisionMakers, onSubmit, renderActionTemplate, action }) => {
  const privacyText = useSel(s => s.taker?.flow?.templateFields?.privacy_text || '')
  const isEditing = useSel(s => s.builder?.flow?.id ? true : false)
  const flowButtonSubmitText = useSel(s => s.taker?.flow?.settings['buttonTextSubmit'] ?? s.builder?.flow?.settings['buttonTextSubmit'])
  const [loading, setLoading] = useState(true)
  const [selectedDecisionMakers, setSelectedDecisionMakers] = useState([])
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [initialBody, setInitialBody] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [postcode, setPostcode] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [town, setTown] = useState('')
  const valid = (subject || '').length > 0 &&
    (body || '').length > 0 &&
    selectedDecisionMakers.length > 0 &&
    (name || '').length > 0 &&
    (email || '').length > 0 &&
    (action.config.requiredUserInfo !== 'name_email' ? (postcode || '').length > 0 : true) &&
    (action.config.requiredUserInfo === 'name_email_postcode_address' ? (addressLine1 || '').length > 0 : true) &&
    (action.config.requiredUserInfo === 'name_email_postcode_address' ? (town || '').length > 0 : true) &&
    (action.config.requireBodyEditing !== true || body != initialBody)

  useEffect(() => {
    renderActionTemplate().then(({ data }) => {
      setSubject(data.subject)
      setBody(data.body)
      setInitialBody(data.body)
      setLoading(false)
      setSelectedDecisionMakers(decisionMakers.map(dm => dm.id))
    })
  }, [])

  function sendEmail() {
    return onSubmit({
      subject: subject,
      body: body,
      decisionMakerIds: selectedDecisionMakers,
      name: name,
      email: email,
      postcode: postcode,
      addressLine1: addressLine1,
      town: town,
    })
  }

  if (loading) return <Loading />

  return <div className="email-editor">
    <p className='title'>Email {decisionMakers.map(dm => dm.name).join(", ")}</p>
    {decisionMakers.length === 1 && selectedDecisionMakers.length === 0 && setSelectedDecisionMakers([decisionMakers[0].id])}
    {decisionMakers.length > 1 && (
      <div className="decision-makers-selection">
        <p className='bold-instruction margin-bottom'>Select people to email:</p>
        <div className="decision-makers-list">
          {decisionMakers.map((dm) => (
            <label key={dm.id} className="decision-maker-checkbox margin-bottom half">
              <input
                type="checkbox"
                checked={selectedDecisionMakers.includes(dm.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDecisionMakers([...selectedDecisionMakers, dm.id]);
                  } else {
                    setSelectedDecisionMakers(selectedDecisionMakers.filter(id => id !== dm.id));
                  }
                }}
                disabled={selectedDecisionMakers.includes(dm.id) && selectedDecisionMakers.length === 1} // Disable if last decision maker
              />
              {dm.name}
            </label>
          ))}
        </div>
      </div>
    )}

    <div className='main-container'>
      {action.config.includeUserInstructions &&
        <div className='left injected-content' dangerouslySetInnerHTML={{ __html: action.config.userInstructionsContent }} />
      }
      <div className='right'>
        <p className='bold-instruction'>Subject</p>
        <input
          className={`subject ${!action.config.editableSubject === true ? 'readonly' : ''}`}
          type='text'
          placeholder='Subject line'
          value={subject}
          onChange={e => setSubject(e.target.value || '')}
          readOnly={!action.config.editableSubject === true}
        />

        <p className='bold-instruction'>Dear {selectedDecisionMakers.map(id => decisionMakers.find(dm => dm.id === id)?.name).join(", ")},</p>
        <textarea
          className={`body ${action.config.editableBody === false && !action.config.requireBodyEditing === true ? 'readonly' : ''}`}
          rows='6'
          value={body}
          onChange={e => setBody(e.target.value || '')}
          readOnly={action.config.editableBody === false && !action.config.requireBodyEditing === true}
          style={{ resize: 'vertical' }}
        ></textarea>
        {(action.config.requireBodyEditing === true && body == initialBody) && <p className='error'>
          You must edit the body of the email before sending.
        </p>}

        <p className='bold-instruction margin-bottom'>Yours sincerely,</p>
        <div className='user-info margin-bottom'>
          <input
            type='text'
            placeholder='Your name*'
            value={name}
            onChange={e => setName(e.target.value || '')}
          ></input>
          <input
            type='email'
            placeholder='Your email*'
            value={email}
            onChange={e => setEmail(e.target.value || '')}
          ></input>
          {action.config.requiredUserInfo === 'name_email_postcode_address' &&
            <>
              <input
                type='text'
                placeholder='First line of address*'
                value={addressLine1}
                onChange={e => setAddressLine1(e.target.value || '')}
              ></input>
              <input
                type='text'
                placeholder='Town*'
                value={town}
                onChange={e => setTown(e.target.value || '')}
              ></input>
            </>
          }
          {action.config.requiredUserInfo !== 'name_email' &&
            <input
              type='text'
              placeholder='Postcode*'
              value={postcode}
              onChange={e => setPostcode(e.target.value || '')}
            ></input>
          }
        </div>

        {action.config.includeFooter &&
          <div className="footer" dangerouslySetInnerHTML={{ __html: action.config.footerContent }} />
        }
        <ServerWaitButton className='margin-top' primary onClick={sendEmail} disabled={!valid}>
          {flowButtonSubmitText || "Send Email"}
        </ServerWaitButton>

        {isEditing ? (
          <>
            <hr className="small-text-divider" />
            <div className='small-text'><FieldEdit slug='privacy_text' /></div>
          </>
        ) : (
          <>
            {privacyText &&
            <>
              <hr className="small-text-divider" />
              <div className='small-text' dangerouslySetInnerHTML={{ __html: privacyText }}></div>
            </>
            }
          </>
        )}
      </div>
    </div>
  </div >
}

export default function EmailToTarget({ action, onSubmit, onSkip }) {
  const [step, setStep] = useState(0)
  const decisionMakerSetId = action.config?.decisionMakerSetId
  const [decisionMakerSet, setDecisionMakerSet] = useState({})
  const [decisionMakers, setDecisionMakers] = useState([])
  const [loading, setLoading] = useState(true)
  const flowId = useSel(s => s.taker?.flow?.id || s.builder.flow.id)
  const actionId = action.id

  useEffect(() => {
    if (!decisionMakerSetId) return;

    restClient.get(`/api/target/decision_maker_sets/${decisionMakerSetId}`)
      .then(({ data }) => {
        setDecisionMakerSet(data)
        setLoading(false)
      })
  }, [decisionMakerSetId])

  function renderActionTemplate() {
    return restClient.get(`/api/target/flows/${flowId}/render_action_template`, { params: { actionId, decisionMakerIds: decisionMakers.map(dm => dm.id) } })
  }

  useEffect(() => {
    if (step === 0 && decisionMakers.length > 0) {
      setStep(1)
    }
  }, [JSON.stringify(decisionMakers)])

  if (loading) return <Loading />

  return <>
    {step === 0 && <LookupDecisionMakers decisionMakerSet={decisionMakerSet} setDecisionMakers={setDecisionMakers} action={action} onSkip={onSkip} />}
    {step === 1 && decisionMakers.length > 0 &&
      <EmailDecisionMakers
        decisionMakers={decisionMakers}
        onSubmit={onSubmit}
        renderActionTemplate={renderActionTemplate}
        action={action}
      />
    }
    {step === 1 && decisionMakers.length === 0 && "No one to email"}
  </>
}
